import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { InvitedTeamMemberState, InvitedTeamMember } from './types'
import { fetchInvitedTeamMembers } from './action'
import { selectFilteredInvitedTeamMembers } from './selector'

export const generateReport = createAsyncThunk(
  'invitedTeamMemberPerEvent/generateReport',
  async (_, { getState }) => {
    try {
      const state = getState() as any
      const eventData = state.invitedTeamMemberPerEvent.data
      const filters = state.invitedTeamMemberPerEvent.filters
      const filteredData = selectFilteredInvitedTeamMembers(state)?.invitedTeamMembers || []

      // Create filter string for filename
      const activeFilters = Object.entries(filters)
        .filter(([_, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}-${value}`)
        .join('_')

      // Convert data to CSV format
      const headers = [
        'Workday ID',
        'Name',
        'Email',
        'Functional Area',
        'Supervisor',
        'Operational Manager',
        'TIDS Practice',
        'Registered?',
      ]
      const csvData = filteredData.map((member: InvitedTeamMember) => [
        member.workdayId,
        member.fullName,
        member.email,
        member.functionalArea,
        member.supervisorName,
        member.operationalManagerName,
        member.tidsPractice,
        member.eventStatus.toLowerCase() === 'registered' ? 'Yes' : 'No',
      ])

      // Helper function to escape CSV values
      const escapeCsvValue = (value: string | null | undefined): string => {
        if (value === null || value === undefined) {
          return ''
        }
        const stringValue = String(value)
        // If the value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`
        }
        return stringValue
      }

      // Create CSV content with proper escaping
      const csvContent = [
        headers.map(escapeCsvValue).join(','),
        ...csvData.map((row: (string | null | undefined)[]) => 
          row.map(escapeCsvValue).join(',')
        )
      ].join('\n')

      // Create Blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Create filename using event title, ID, and filters
      const fileName = `${eventData.eventTitle.replace(/[^a-zA-Z0-9]/g, '_')}_${eventData.eventId}${activeFilters ? '_' + activeFilters : ''}_invited_members_report.csv`
      link.setAttribute('download', fileName)

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return true
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Error generating CSV report:', error.message)
      } else {
        console.error('Unknown error generating CSV report')
      }
      throw error
    }
  },
)

const initialState: InvitedTeamMemberState = {
  data: null,
  loading: false,
  error: null,
  filters: {
    eventStatus: undefined,
    searchQuery: undefined,
    jobProfile: undefined,
    functionalArea: undefined,
    supervisorName: undefined,
    operationalManagerName: undefined,
    tidsPractice: undefined,
  },
}

const invitedTeamMemberSlice = createSlice({
  name: 'invitedTeamMemberPerEvent',
  initialState,
  reducers: {
    clearInvitedTeamMembers: (state) => {
      state.data = null
      state.error = null
    },
    setInvitedTeamMembers: (state, action: PayloadAction<InvitedTeamMember[]>) => {
      if (state.data) {
        state.data.invitedTeamMembers = action.payload
      } else {
        state.data = {
          eventId: '',
          eventTitle: '',
          targetCompliance: 0,
          totalInvited: action.payload.length,
          invitedTeamMembers: action.payload,
        }
      }
    },
    setEventStatusFilter: (state, action) => {
      state.filters.eventStatus = action.payload
    },
    setSearchQuery: (state, action) => {
      state.filters.searchQuery = action.payload
    },
    setJobProfileFilter: (state, action) => {
      state.filters.jobProfile = action.payload
    },
    setFunctionalAreaFilter: (state, action) => {
      state.filters.functionalArea = action.payload
    },
    setSupervisorNameFilter: (state, action) => {
      state.filters.supervisorName = action.payload
    },
    setOperationalManagerNameFilter: (state, action) => {
      state.filters.operationalManagerName = action.payload
    },
    setTidsPracticeFilter: (state, action) => {
      state.filters.tidsPractice = action.payload
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvitedTeamMembers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchInvitedTeamMembers.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload
        state.error = null
      })
      .addCase(fetchInvitedTeamMembers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(generateReport.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(generateReport.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const {
  clearInvitedTeamMembers,
  setInvitedTeamMembers,
  setEventStatusFilter,
  setSearchQuery,
  setJobProfileFilter,
  setFunctionalAreaFilter,
  setSupervisorNameFilter,
  setOperationalManagerNameFilter,
  setTidsPracticeFilter,
  clearFilters,
} = invitedTeamMemberSlice.actions

export default invitedTeamMemberSlice.reducer
